import { Box } from '@mui/material';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useMutation } from 'react-query';
import { useRecoilStateLoadable } from 'recoil';
import * as Yup from 'yup';

import { FormikTextField } from '../../../common/formik-material-ui';
import { trackSaveAddExternalNumber } from '../../../common/userTracking';
import { user } from '../../../recoil/atoms/user';
import { getApiMutation } from '../../../services/rest/getApiMutation';
import { endpointEnum } from '../../../services/rest/makeAPIRequest';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';

import { ModalWithStyles } from './ModalWithStyles';

export function AddExtModal({
  show,
  handleClose,
  enqueueSnackbarWithStyles,
  methodNameEnum,
  refetchBusinessNumbers,
  isDirectoryNumber = false,
}) {
  const portSchema = useMemo(
    () =>
      Yup.object({
        did: Yup.string()
          .required('Required')
          .min(5, 'Number must be a minimum of 5 characters')
          .max(15, 'Number must be a maximum of 15 characters')
          .matches(
            /^[1-9]\d{1,14}$/g,
            'Invalid Phone Number.  Please use E.164 phone number format.'
          ),
        numberName: Yup.string().max(
          100,
          'Name must be a maximum of 100 characters'
        ),
      }),
    []
  );
  const [userAtom, setUserAtom] = useRecoilStateLoadable(user);
  const mutation = useMutation(getApiMutation(userAtom.contents, setUserAtom));

  const { treatment } = useFeatureFlag('DASH-172-number-input-is-cut-off');
  const isPreventCutOffEnabled = useMemo(
    () => treatment === 'control' || treatment === 'on',
    [treatment]
  );

  return (
    <Formik
      onSubmit={async (values, actions) => {
        let response;
        const data = {
          call: isDirectoryNumber
            ? 'insertExternalDirectory'
            : methodNameEnum.ADD_EXTERNAL,
          apikey: userAtom.contents?.apikey,
          did: values.did,
          numberName: values.numberName,
        };
        if (isDirectoryNumber) {
          response = await mutation.mutateAsync({
            endpoint: endpointEnum.ADD_EXTERNAL_DIRECTORY,
            payload: data,
          });
        } else {
          response = await mutation.mutateAsync({
            endpoint: endpointEnum.ADD_PORT,
            payload: data,
          });
        }
        if (response?.data?.success) {
          enqueueSnackbarWithStyles({
            message: `Successfully Added External Number ${values.did}.`,
            alertProps: { severity: 'success' },
          });
        } else {
          enqueueSnackbarWithStyles({
            message: `Duplicate Number ${values.did}.`,
            alertProps: { severity: 'warning' },
          });
        }
        refetchBusinessNumbers();
        handleClose();
        actions.resetForm({});
      }}
      validationSchema={portSchema}
      enableReinitialize={true}
      initialValues={{
        did: '',
        numberName: '',
      }}
    >
      {(formik) => (
        <ModalWithStyles
          show={show}
          onSubmit={() => {
            trackSaveAddExternalNumber();
            formik.handleSubmit();
          }}
          title={
            isDirectoryNumber
              ? 'Add an External Number to the Directory'
              : 'Add an External Number'
          }
          aria-labelledby="example-modal-sizes-title-sm"
          onHide={() => {
            formik.resetForm({});
            handleClose();
          }}
          disableSubmit={!formik.isValid}
          isSubmitting={formik.isSubmitting}
          isLoading={formik.isSubmitting}
          size="xs"
        >
          <Box
            {...(isPreventCutOffEnabled && {
              sx: {
                display: 'flex',
                flexDirection: 'column',
                marginTop: '20px',
                gap: '10px',
              },
            })}
          >
            <FormikTextField
              id="standard-basic"
              label={
                isDirectoryNumber
                  ? 'Directory External Number'
                  : 'External Number'
              }
              helperText={
                isDirectoryNumber
                  ? 'The Non-Kixie Phone Number that will be stored in the PowerCall Directory'
                  : 'Use E.164 phone number format (No Plus)'
              }
              name="did"
              formik={formik}
              size="small"
              variant="outlined"
              required={true}
              fullWidth
            />
            <FormikTextField
              id="standard-basic"
              label="Name"
              name="numberName"
              helperText={
                isDirectoryNumber
                  ? 'The name that will appear in the PowerCall Directory for this number'
                  : ''
              }
              formik={formik}
              size="small"
              variant="outlined"
              fullWidth
              required={isDirectoryNumber}
            />
            {!isPreventCutOffEnabled && <br />}
            {!isPreventCutOffEnabled && <br />}
          </Box>
        </ModalWithStyles>
      )}
    </Formik>
  );
}
